<template>
  <div class="finance-preview-container">
    <!-- 顶部标题栏 -->
    <div class="page-header">
      <div class="header-title">
        <div class="title-mark"></div>
        <span>台账详情</span>
      </div>
      <div class="header-actions">
        <el-button size="small" type="warning" icon="el-icon-s-data" @click="handleViewFlows">流水查看</el-button>
        <el-button size="small" type="primary" icon="el-icon-edit" @click="handleEdit">编辑台账</el-button>
        <el-button size="small" type="primary" icon="el-icon-back" @click="back">返回列表</el-button>
      </div>
    </div>

    <!-- 加载中状态 -->
    <div v-if="loading" class="loading-container">
      <el-skeleton :rows="10" animated />
    </div>

    <!-- 台账信息 -->
    <div v-else>
      <!-- 台账基本信息 -->
      <el-card class="box-card">
        <div slot="header" class="card-header">
          <span>台账基本信息</span>
        </div>
        <el-descriptions border :column="3">
          <el-descriptions-item label="台账编号">{{ fundData.ledgerNumber || '-' }}</el-descriptions-item>
          <el-descriptions-item label="状态">
            <el-tag :type="getStatusTagType(fundData.status)">{{ translateStatus(fundData.status) }}</el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="合同名称" v-if="fundData.contractName">{{ fundData.contractName }}</el-descriptions-item>
          <el-descriptions-item label="合同编号" v-if="fundData.contractNumber">{{ fundData.contractNumber }}</el-descriptions-item>
          <el-descriptions-item label="供应商" v-if="fundData.supplierUnitName">{{ fundData.supplierUnitName }}</el-descriptions-item>
          <el-descriptions-item label="需求方" v-if="fundData.demandUnitNames">{{ fundData.demandUnitNames }}</el-descriptions-item>
          <el-descriptions-item label="创建时间">{{ formatDateTime(fundData.createTime) }}</el-descriptions-item>
          <el-descriptions-item label="创建人" v-if="fundData.creatorName">{{ fundData.creatorName }}</el-descriptions-item>
          <el-descriptions-item label="应付日期时间依据">{{ translateDueDateBasis(fundData.dueDateBasis) }}</el-descriptions-item>
          <el-descriptions-item label="备注" :span="3">{{ fundData.remark || '无' }}</el-descriptions-item>
        </el-descriptions>
      </el-card>

      <!-- 收付款记录列表 -->
      <el-card class="box-card" style="margin-top: 20px;">
        <div slot="header" class="card-header">
          <span>收付款记录 (共{{ paymentRecords.length }}笔)</span>
          <div class="header-actions-inner">
            <div class="total-amount">
              <span>总金额：</span>
              <span class="amount-value">{{ formatNumber(totalAmount) }}</span>
            </div>
            <el-button 
              type="primary" 
              size="small" 
              icon="el-icon-plus"
              @click="handleAddItem"
            >添加收付款记录</el-button>
          </div>
        </div>
        
        <!-- 无收付款记录时的空状态引导 -->
        <div v-if="!loading && paymentRecords.length === 0" class="empty-items-guide">
          <el-empty description="暂无收付款记录，请添加首笔收付款记录">
            <el-button
              type="primary"
              icon="el-icon-plus"
              @click="handleAddItem"
            >添加首笔收付款记录</el-button>
            <p class="empty-tip">保存台账后，在此处逐笔添加每笔收/付款（选择结算单、本笔金额、收付款时间等）</p>
          </el-empty>
        </div>
        <el-table
          v-else
          :data="paymentRecords"
          border
          style="width: 100%;"
          v-loading="loading"
          row-key="id"
        >
          <el-table-column type="expand">
            <template slot-scope="scope">
              <div class="ledger-flows-expand">
                <div class="expand-title">本笔分摊流水（按时间依据分配到各结算单）</div>
                <el-table :data="scope.row.ledgerFlows || []" border size="mini">
                  <el-table-column label="结算单号" prop="settlementNumber" min-width="140" align="center" />
                  <el-table-column label="分摊金额" width="140" align="center">
                    <template slot-scope="inner">
                      <span class="amount-text">{{ formatNumber(inner.row.amount) }}</span>
                    </template>
                  </el-table-column>
                </el-table>
              </div>
            </template>
          </el-table-column>
          <el-table-column type="index" label="序号" width="60" align="center" />
          <el-table-column label="资金类型" width="90" align="center">
            <template slot-scope="scope">
              {{ (scope.row.fundType || scope.row.fund_type) === 'PAYABLE' ? '付款' : '收款' }}
            </template>
          </el-table-column>
          <el-table-column label="本笔总金额" width="140" align="center">
            <template slot-scope="scope">
              <span class="amount-text">{{ formatNumber(scope.row.amount) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="收付款时间" width="170" align="center">
            <template slot-scope="scope">
              {{ formatDateTime(scope.row.paymentDate) }}
            </template>
          </el-table-column>
          <el-table-column label="支付方式" width="110" align="center">
            <template slot-scope="scope">
              {{ scope.row.paymentMethod || '-' }}
            </template>
          </el-table-column>
          <el-table-column label="分摊结算单数" width="110" align="center">
            <template slot-scope="scope">
              {{ (scope.row.ledgerFlows || []).length }} 个
            </template>
          </el-table-column>
          <el-table-column label="附件" width="90" align="center">
            <template slot-scope="scope">
              <el-button
                v-if="scope.row.attachmentPaths && scope.row.attachmentPaths.length > 0"
                type="text"
                size="mini"
                icon="el-icon-paperclip"
                @click="handleViewAttachments(scope.row)"
              >
                {{ scope.row.attachmentPaths.length }}个
              </el-button>
              <span v-else style="color: #909399;">无</span>
            </template>
          </el-table-column>
          <el-table-column label="备注" min-width="140" align="center" show-overflow-tooltip>
            <template slot-scope="scope">
              {{ scope.row.remark || '-' }}
            </template>
          </el-table-column>
          <el-table-column label="操作" width="150" align="center" fixed="right">
            <template slot-scope="scope">
              <el-button
                type="primary"
                size="mini"
                v-permission="'finance:finance-list:edit'"
                @click="handleEditItem(scope.row)"
              >编辑</el-button>
              <el-button
                type="danger"
                size="mini"
                v-permission="'finance:finance-list:delete'"
                @click="handleDeleteItem(scope.row)"
              >删除</el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-card>
    </div>

    <!-- 添加收付款记录：多选结算单 + 本笔总金额，系统按时间依据自动分摊 -->
    <el-dialog
      :title="itemDialogTitle"
      :visible.sync="itemDialogVisible"
      width="620px"
      :close-on-click-modal="false"
      @close="resetItemForm"
    >
      <el-form :model="itemForm" :rules="itemRules" ref="itemForm" label-width="140px">
        <el-form-item label="资金类型" prop="fundType">
          <el-radio-group v-model="itemForm.fundType">
            <el-radio label="RECEIVABLE">收款</el-radio>
            <el-radio label="PAYABLE">付款</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item v-if="!itemForm.id" label="结算单（多选）" prop="settlementIds">
          <el-select
            v-model="itemForm.settlementIds"
            placeholder="请选择多个结算单，按时间依据自动分摊"
            filterable
            multiple
            style="width: 100%"
            value-key="id"
          >
            <el-option
              v-for="s in availableSettlements"
              :key="s.id"
              :label="getSettlementOptionLabel(s)"
              :value="s.id"
            />
          </el-select>
          <div class="form-tip">本笔总金额将按台账时间依据（如结算时间）分配到所选结算单</div>
        </el-form-item>
        <el-form-item :label="labelAmount" prop="amount">
          <el-input-number
            v-if="!itemForm.id"
            v-model="itemForm.amount"
            :precision="2"
            :min="0"
            :max="999999999.99"
            :placeholder="'请输入本笔总金额（≤ 所选结算单金额之和）'"
            style="width: 100%"
            @change="validateAmount"
          />
          <el-input v-else :value="formatNumber(itemForm.amount)" disabled style="width: 100%" />
          <div v-if="!itemForm.id && selectedSettlementsSum !== null" class="form-tip">
            所选结算单金额合计：{{ formatNumber(selectedSettlementsSum) }}；本笔须 ≤ 合计
          </div>
          <div v-if="itemForm.id" class="form-tip">修改金额或结算单请删除本笔后重新添加</div>
        </el-form-item>
        <el-form-item :label="labelPaymentDate" prop="paymentDate">
          <el-date-picker
            v-model="itemForm.paymentDate"
            type="datetime"
            :placeholder="'请选择' + (itemForm.fundType === 'RECEIVABLE' ? '收款' : '付款') + '时间'"
            format="yyyy-MM-dd HH:mm:ss"
            value-format="yyyy-MM-dd HH:mm:ss"
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item label="支付方式">
          <el-select v-model="itemForm.paymentMethod" placeholder="请选择支付方式" style="width: 100%">
            <el-option label="转账支付" value="转账支付" />
            <el-option label="银行票据" value="银行票据" />
            <el-option label="金融产品" value="金融产品" />
            <el-option label="其他形式" value="其他形式" />
          </el-select>
        </el-form-item>
        <el-form-item label="附件">
          <el-upload
            ref="attachmentUpload"
            class="attachment-uploader"
            action="#"
            :http-request="handleAttachmentUpload"
            :before-upload="beforeFileUpload"
            :file-list="itemForm.attachmentList"
            :on-remove="handleRemoveAttachment"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.zip,.rar"
          >
            <el-button type="primary" size="small" icon="el-icon-upload">上传附件</el-button>
            <div slot="tip" class="el-upload__tip">支持 PDF、Word、Excel、图片等，单文件不超过50MB</div>
          </el-upload>
        </el-form-item>
        <el-form-item label="备注">
          <el-input
            v-model="itemForm.remark"
            type="textarea"
            :rows="3"
            placeholder="请输入备注"
            maxlength="500"
            show-word-limit
          />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="itemDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveItem" :loading="savingItem">保存</el-button>
      </div>
    </el-dialog>

    <!-- 附件查看对话框 -->
    <el-dialog
      title="附件列表"
      :visible.sync="attachmentDialogVisible"
      width="60%"
      :close-on-click-modal="false"
    >
      <div v-if="currentAttachments && currentAttachments.length > 0" class="attachment-list">
        <el-table :data="currentAttachments" border style="width: 100%">
          <el-table-column label="附件名称" prop="name" min-width="200"></el-table-column>
          <el-table-column label="操作" width="120" align="center">
            <template slot-scope="scope">
              <el-button
                v-permission="'finance:finance-list:download-attachment'"
                type="text"
                size="mini"
                icon="el-icon-download"
                @click="handleDownloadAttachment(scope.row)"
              >
                下载
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <div v-else class="empty-attachments">
        <p>暂无附件</p>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="attachmentDialogVisible = false">关 闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { 
  query_fund_ledger_by_id, 
  add_payment_record, 
  update_payment_record, 
  delete_payment_record,
  query_settlements_by_ledger_id
} from '@/api/modules/fund-ledger'
import { upload_attachment, download_attachment } from '@/api/modules/attachment'

export default {
  name: 'FinancePreview',
  data() {
    return {
      fundId: null,
      loading: false,
      savingItem: false,
      fundData: {
        id: null,
        ledgerNumber: '',
        status: '',
        contractId: null,
        contractName: '',
        contractNumber: '',
        demandUnitNames: '',
        supplierUnitName: '',
        remark: '',
        createTime: '',
        creatorName: ''
      },
      paymentRecords: [],
      availableSettlements: [],
      itemDialogVisible: false,
      itemDialogTitle: '添加收付款记录',
      itemForm: {
        id: null,
        fundType: 'RECEIVABLE',
        settlementIds: [],
        amount: null,
        paymentDate: '',
        paymentMethod: '',
        attachmentPaths: [],
        attachmentNames: [],
        attachmentList: [],
        remark: ''
      },
      itemRules: {
        fundType: [
          { required: true, message: '请选择资金类型', trigger: 'change' }
        ],
        settlementIds: [
          { type: 'array', required: true, message: '请至少选择一个结算单', trigger: 'change', min: 1 }
        ],
        amount: [
          { required: true, message: '请输入本笔总金额', trigger: 'blur' },
          { type: 'number', min: 0.01, message: '金额必须大于0', trigger: 'blur' }
        ],
        paymentDate: [
          { required: true, message: '请选择收付款时间', trigger: 'change' }
        ]
      },
      attachmentDialogVisible: false,
      currentAttachments: []
    }
  },
  computed: {
    totalAmount() {
      return this.paymentRecords.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0)
    },
    labelAmount() {
      return this.itemForm.fundType === 'RECEIVABLE' ? '本笔收款总金额' : '本笔付款总金额'
    },
    labelPaymentDate() {
      return this.itemForm.fundType === 'RECEIVABLE' ? '收款时间' : '付款时间'
    },
    selectedSettlementsSum() {
      if (!this.itemForm.settlementIds || this.itemForm.settlementIds.length === 0) return null
      const sum = this.itemForm.settlementIds.reduce((acc, id) => {
        const s = this.availableSettlements.find(x => x.id === id)
        return acc + (s && s.amount != null ? parseFloat(s.amount) : 0)
      }, 0)
      return sum
    }
  },
  created() {
    const id = this.$route.params.id
    if (id) {
      this.fundId = id
      this.fetchFundDetails()
    } else {
      this.$message.warning('缺少台账ID参数')
      this.back()
    }
  },
  methods: {
    // 获取台账详情
    async fetchFundDetails() {
      if (!this.fundId) return
      
      this.loading = true
      try {
        const response = await query_fund_ledger_by_id(this.fundId)
        
        if (response.data && response.data.success) {
          const ledger = response.data.data
          
          this.fundData = {
            id: ledger.id,
            ledgerNumber: ledger.ledgerNumber || '',
            status: ledger.status || 'ACTIVE',
            contractId: ledger.contractId,
            contractName: ledger.contractName || '',
            contractNumber: ledger.contractNumber || '',
            demandUnitNames: ledger.demandUnitNames || '',
            supplierUnitName: ledger.supplierUnitName || '',
            dueDateBasis: ledger.dueDateBasis || ledger.due_date_basis || 'SETTLEMENT_TIME',
            remark: ledger.remark || '',
            createTime: ledger.createTime || '',
            creatorName: ledger.creatorName || ''
          }
          
          // 收付款记录（新结构：paymentRecords，每条含 ledgerFlows）
          this.paymentRecords = (ledger.paymentRecords || ledger.fundLedgerItems || []).map(item => ({
            id: item.id,
            fundLedgerId: item.fundLedgerId,
            fundType: item.fundType || item.fund_type || 'RECEIVABLE',
            amount: item.amount || 0,
            paymentDate: item.paymentDate || '',
            paymentMethod: item.paymentMethod || '',
            attachmentPaths: item.attachmentPaths || [],
            attachmentNames: item.attachmentNames || [],
            remark: item.remark || '',
            ledgerFlows: item.ledgerFlows || [],
            createTime: item.createTime || ''
          }))
          
          // 加载可用结算单列表（根据台账ID查询合同相关结算单）
          this.loadAvailableSettlements()
        } else {
          this.$message.error('获取台账详情失败: ' + ((response.data && response.data.message) || '未知错误'))
        }
      } catch (error) {
        console.error('获取台账详情失败:', error)
        this.$message.error('获取台账详情失败: ' + (error.response?.data?.message || error.message || '未知错误'))
      } finally {
        this.loading = false
      }
    },
    
    // 加载可用结算单列表（根据台账ID查询合同相关结算单，使用专用接口）
    async loadAvailableSettlements() {
      if (!this.fundId) {
        this.availableSettlements = []
        return
      }
      
      try {
        const res = await query_settlements_by_ledger_id(this.fundId)
        let list = []
        if (res.data && res.data.success) {
          list = Array.isArray(res.data.data) ? res.data.data : []
        }
        
        this.availableSettlements = list.map(s => ({
          id: s.id || s.settlement_id,
          settlementNumber: s.settlementNumber || s.settlement_number || '',
          settlementTitle: s.settlementTitle || s.settlement_title || '',
          settlementDate: s.settlementDate || s.settlement_date || s.create_time || '',
          amount: s.amount != null ? s.amount : 0
        }))
      } catch (error) {
        console.error('加载结算单列表失败:', error)
        this.availableSettlements = []
      }
    },
    
    validateAmount() {
      if (this.selectedSettlementsSum == null || this.itemForm.amount == null) return
      if (this.itemForm.amount > this.selectedSettlementsSum) {
        this.$message.warning('本笔金额不能大于所选结算单金额之和')
      }
    },
    // 添加收付款记录
    handleAddItem() {
      this.itemDialogTitle = '添加收付款记录'
      this.resetItemForm()
      this.itemDialogVisible = true
    },
    
    // 编辑收付款记录（仅可改：收付款时间、支付方式、附件、备注）
    handleEditItem(row) {
      this.itemDialogTitle = '编辑收付款记录'
      this.itemForm = {
        id: row.id,
        fundType: row.fundType || row.fund_type || 'RECEIVABLE',
        settlementIds: (row.ledgerFlows || []).map(f => f.settlementId),
        amount: row.amount,
        paymentDate: row.paymentDate,
        paymentMethod: row.paymentMethod || '',
        attachmentPaths: row.attachmentPaths || [],
        attachmentNames: row.attachmentNames || [],
        attachmentList: (row.attachmentPaths || []).map((path, index) => ({
          name: row.attachmentNames[index] || path.split('/').pop(),
          url: path
        })),
        remark: row.remark || ''
      }
      this.itemDialogVisible = true
    },
    
    // 删除收付款记录
    handleDeleteItem(row) {
      this.$confirm('确认删除该收付款记录?', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          await delete_payment_record(row.id)
          this.$message.success('删除成功!')
          this.fetchFundDetails()
        } catch (error) {
          console.error('删除收付款记录失败:', error)
          this.$message.error('删除失败: ' + (error.response?.data?.message || error.message || '未知错误'))
        }
      }).catch(() => {})
    },
    
    // 保存收付款记录
    async saveItem() {
      this.$refs.itemForm.validate(async (valid) => {
        if (!valid) {
          this.$message.error('请完善表单信息')
          return
        }
        if (!this.itemForm.id) {
          if (!this.itemForm.settlementIds || this.itemForm.settlementIds.length === 0) {
            this.$message.error('请至少选择一个结算单')
            return
          }
          if (this.selectedSettlementsSum != null && this.itemForm.amount > this.selectedSettlementsSum) {
            this.$message.error('本笔金额不能大于所选结算单金额之和')
            return
          }
        }
        this.savingItem = true
        try {
          if (this.itemForm.id) {
            const requestData = {
              paymentDate: this.itemForm.paymentDate,
              paymentMethod: this.itemForm.paymentMethod,
              attachmentPaths: this.itemForm.attachmentPaths,
              attachmentNames: this.itemForm.attachmentNames,
              remark: this.itemForm.remark
            }
            const response = await update_payment_record(this.itemForm.id, requestData)
            if (response.data && response.data.success) {
              this.$message.success('更新成功!')
              this.itemDialogVisible = false
              this.fetchFundDetails()
            } else {
              throw new Error((response.data && response.data.message) || '更新失败')
            }
          } else {
            const requestData = {
              fundType: this.itemForm.fundType,
              amount: this.itemForm.amount,
              paymentDate: this.itemForm.paymentDate,
              settlementIds: this.itemForm.settlementIds,
              paymentMethod: this.itemForm.paymentMethod,
              attachmentPaths: this.itemForm.attachmentPaths,
              attachmentNames: this.itemForm.attachmentNames,
              remark: this.itemForm.remark
            }
            const response = await add_payment_record(this.fundId, requestData)
            if (response.data && response.data.success) {
              this.$message.success('添加成功!')
              this.itemDialogVisible = false
              this.fetchFundDetails()
            } else {
              throw new Error((response.data && response.data.message) || '添加失败')
            }
          }
        } catch (error) {
          console.error('保存收付款记录失败:', error)
          this.$message.error('保存失败: ' + (error.response?.data?.message || error.message || '未知错误'))
        } finally {
          this.savingItem = false
        }
      })
    },
    
    // 重置收付款记录表单
    resetItemForm() {
      this.itemForm = {
        id: null,
        fundType: 'RECEIVABLE',
        settlementIds: [],
        amount: null,
        paymentDate: '',
        paymentMethod: '',
        attachmentPaths: [],
        attachmentNames: [],
        attachmentList: [],
        remark: ''
      }
      if (this.$refs.itemForm) {
        this.$refs.itemForm.clearValidate()
      }
    },
    
    // 文件上传前验证
    beforeFileUpload(file) {
      const maxSize = 50 * 1024 * 1024
      const allowedTypes = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'jpg', 'jpeg', 'png', 'gif', 'zip', 'rar']
      
      if (file.size > maxSize) {
        this.$message.error('文件大小不能超过50MB')
        return false
      }
      
      const fileExtension = file.name.split('.').pop().toLowerCase()
      if (!allowedTypes.includes(fileExtension)) {
        this.$message.error('不支持的文件类型')
        return false
      }
      
      return true
    },
    
    // 自定义上传方法
    async handleAttachmentUpload(options) {
      const file = options.file
      
      try {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('category', 'fund-ledger')
        formData.append('relatedId', this.itemForm.id || this.fundId || 0)
        
        const response = await upload_attachment(formData)
        
        if (response.data && response.data.success) {
          const filePath = response.data.data.filePath
          const originalName = response.data.data.originalName || file.name
          
          this.itemForm.attachmentPaths.push(filePath)
          this.itemForm.attachmentNames.push(originalName)
          this.itemForm.attachmentList.push({
            name: originalName,
            url: filePath
          })
          
          this.$message.success(`附件 "${originalName}" 上传成功`)
          options.onSuccess()
        } else {
          throw new Error((response.data && response.data.message) || '上传失败')
        }
      } catch (error) {
        console.error('上传附件失败:', error)
        this.$message.error('上传附件失败: ' + (error.response?.data?.message || error.message || '未知错误'))
        options.onError(error)
      }
    },
    
    // 移除附件
    handleRemoveAttachment(file, fileList) {
      const index = this.itemForm.attachmentList.findIndex(item => item.name === file.name)
      if (index > -1) {
        this.itemForm.attachmentPaths.splice(index, 1)
        this.itemForm.attachmentNames.splice(index, 1)
        this.itemForm.attachmentList = fileList
      }
    },
    
    // 查看附件
    handleViewAttachments(row) {
      this.currentAttachments = (row.attachmentPaths || []).map((path, index) => ({
        name: row.attachmentNames[index] || path.split('/').pop(),
        file_path: path
      }))
      this.attachmentDialogVisible = true
    },
    
    // 下载附件
    async handleDownloadAttachment(attachment) {
      if (!attachment.file_path) {
        this.$message.error('附件路径不存在，无法下载')
        return
      }
      
      try {
        const response = await download_attachment(attachment.file_path, attachment.name)
        
        const blob = new Blob([response.data])
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = attachment.name || 'download'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        
        this.$message.success('附件下载成功')
      } catch (error) {
        console.error('下载附件失败:', error)
        this.$message.error('下载附件失败: ' + (error.response?.data?.message || error.message || '未知错误'))
      }
    },
    
    // 编辑台账
    handleEdit() {
      this.$router.push({ path: `/finance/finance-edit/${this.fundId}` })
    },
    // 流水查看
    handleViewFlows() {
      this.$router.push({ path: `/finance/ledger-flows/${this.fundId}` })
    },
    // 返回列表
    back() {
      this.$router.push({ path: '/finance/finance-list' })
    },
    
    // 格式化日期时间
    formatDateTime(dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      if (isNaN(date.getTime())) return dateStr
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }).replace(/\//g, '-')
    },
    
    // 格式化数字
    formatNumber(value) {
      if (value === undefined || value === null) return '0.00'
      const num = parseFloat(value)
      if (isNaN(num)) return '0.00'
      return num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },
    
    // 翻译状态
    translateDueDateBasis(value) {
      const map = {
        'SETTLEMENT_TIME': '结算时间',
        'SHIPMENT_TIME': '发货时间',
        'RECEIPT_TIME': '收货时间'
      }
      return map[value] || value || '-'
    },
    translateStatus(status) {
      const statusMap = {
        'ACTIVE': '正常',
        'REMOVE': '已删除'
      }
      return statusMap[status] || status
    },
    
    // 获取状态标签类型
    getStatusTagType(status) {
      const statusMap = {
        'ACTIVE': 'success',
        'REMOVE': 'danger'
      }
      return statusMap[status] || ''
    },
    
    // 结算单下拉选项展示：结算单号/标题、结算时间、金额
    getSettlementOptionLabel(settlement) {
      if (!settlement) return ''
      const name = settlement.settlementNumber || settlement.settlementTitle || settlement.settlement_title || '-'
      const date = settlement.settlementDate || settlement.settlement_date || settlement.create_time || '-'
      const dateStr = date === '-' ? date : this.formatDateTime(date)
      const amount = settlement.amount != null ? this.formatNumber(settlement.amount) : '-'
      return `${name} | ${dateStr} | 金额：${amount}`
    }
  }
}
</script>

<style lang="scss" scoped>
.finance-preview-container {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  
  .header-title {
    display: flex;
    align-items: center;
    font-size: 18px;
    font-weight: bold;
    
    .title-mark {
      width: 4px;
      height: 18px;
      background: #409EFF;
      margin-right: 10px;
    }
  }
  
  .header-actions {
    display: flex;
    gap: 10px;
  }
}

.loading-container {
  padding: 20px;
}

.box-card {
  margin-bottom: 20px;
  
  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 16px;
    font-weight: bold;
    
    .header-actions-inner {
      display: flex;
      align-items: center;
      gap: 20px;
    }
    
    .total-amount {
      font-size: 14px;
      color: #606266;
      
      .amount-value {
        font-size: 18px;
        font-weight: bold;
        color: #f56c6c;
        margin-left: 5px;
      }
    }
  }
}

.amount-text {
  color: #f56c6c;
  font-weight: bold;
  font-size: 14px;
}

.attachment-list {
  margin-top: 10px;
}

.empty-attachments {
  padding: 40px 0;
  text-align: center;
  color: #909399;
}

.empty-items-guide {
  padding: 40px 20px;
  text-align: center;
  background: #fafafa;
  border-radius: 4px;
  margin: 10px 0;

  .empty-tip {
    margin-top: 16px;
    font-size: 13px;
    color: #909399;
    line-height: 1.5;
  }
}

.attachment-uploader {
  width: 100%;
}

.form-tip {
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
}

.ledger-flows-expand {
  padding: 12px 20px;
  background: #fafafa;
  .expand-title {
    font-size: 13px;
    color: #606266;
    margin-bottom: 8px;
  }
}

::v-deep .el-table {
  font-size: 14px;
  
  .el-table__header th {
    background-color: #f5f7fa;
    color: #606266;
    font-weight: 600;
  }
}

::v-deep .el-descriptions {
  .el-descriptions__label {
    font-weight: 600;
    font-size: 14px;
    color: #606266;
  }
  
  .el-descriptions__content {
    font-size: 14px;
    color: #303133;
  }
}
</style>
